// product-block-cart.js
class ProductBlockCart {
  constructor(productBlock) {
    this.productBlock = productBlock;
    this.productId = productBlock.dataset.productId;
    this.productHandle = productBlock.dataset.productHandle;
    this.variantInput = productBlock.querySelector('input[data-variant-input]');
    this.addToCartBtn = productBlock.querySelector('button[data-add-to-cart]');
    this.buttonText = productBlock.querySelector('[data-button-text]');
    this.buttonLoading = productBlock.querySelector('[data-button-loading]');
    this.productForm = productBlock.querySelector('form[data-product-form]');
    
    if (!this.variantInput || !this.addToCartBtn) return;
    
    this.variantsData = this.getVariantsData();
    this.init();
  }
  
  getVariantsData() {
    const variantsScript = this.productBlock.querySelector('script[data-product-variants]');
    if (!variantsScript) return {};
    
    try {
      return JSON.parse(variantsScript.textContent);
    } catch (e) {
      console.error('Error parsing variants data:', e);
      return {};
    }
  }
  
  init() {
    this.setupSwatches();
    this.setupAddToCart();
    this.setupDefaultVariant();
  }
  
  setupSwatches() {
    const optionContainers = this.productBlock.querySelectorAll('.product-block-options');
    
    // Set initial active swatch
    optionContainers.forEach(container => {
      const firstAvailable = container.querySelector('.product-block-options__item:not(.product-block-options__item--unavailable)');
      if (firstAvailable && !container.querySelector('.product-block-options__item--active')) {
        firstAvailable.classList.add('product-block-options__item--active');
      }
    });
    
    // Handle swatch clicks
    this.productBlock.addEventListener('click', (e) => {
      const swatchItem = e.target.closest('.product-block-options__item');
      if (!swatchItem || swatchItem.classList.contains('product-block-options__item--unavailable')) return;
      
      const optionContainer = swatchItem.closest('.product-block-options');
      if (!optionContainer) return;
      
      optionContainer.querySelectorAll('.product-block-options__item').forEach(item => {
        item.classList.remove('product-block-options__item--active');
      });
      swatchItem.classList.add('product-block-options__item--active');
      
      this.updateVariantFromSwatches();
    });
  }
  
  setupAddToCart() {
    if (!this.productForm) return;
    
    this.productForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.addToCart();
    });
  }
  
  setupDefaultVariant() {
    const defaultVariantId = this.variantInput.dataset.defaultVariant;
    if (defaultVariantId) {
      this.variantInput.value = defaultVariantId;
    }
  }
  
  getSelectedOptions() {
    const selectedOptions = {};
    const optionElements = this.productBlock.querySelectorAll('.product-block-options');
    
    optionElements.forEach((optionEl) => {
      const optionName = optionEl.dataset.optionName;
      const selectedItem = optionEl.querySelector('.product-block-options__item--active');
      
      if (selectedItem) {
        selectedOptions[optionName] = selectedItem.dataset.optionValue;
      }
    });
    
    return selectedOptions;
  }
  
  findVariantByOptions(selectedOptions) {
    if (!this.variantsData.variants) return null;
    
    for (const variant of this.variantsData.variants) {
      let match = true;
      
      for (const optionName in selectedOptions) {
        if (variant.options[optionName] !== selectedOptions[optionName]) {
          match = false;
          break;
        }
      }
      
      if (match) {
        return variant;
      }
    }
    
    return null;
  }
  
  updateVariantFromSwatches() {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariantByOptions(selectedOptions);
    
    if (variant) {
      this.variantInput.value = variant.id;
      this.updateButtonState(variant.available);
    }
  }
  
  updateButtonState(isAvailable) {
    if (isAvailable) {
      this.addToCartBtn.disabled = false;
      if (this.buttonText) {
        this.buttonText.textContent = theme.strings.addToCart;
      }
    } else {
      this.addToCartBtn.disabled = true;
      if (this.buttonText) {
        this.buttonText.textContent = theme.strings.soldOut;
      }
    }
  }
  
  async addToCart() {
    if (this.addToCartBtn.disabled) return;
    
    const variantId = this.variantInput.value;
    const quantity = 1;
    
    // Show loading state
    this.showLoadingState();
    
    try {
      // Add to cart using Shopify's AJAX API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          sections: ['cart-drawer', 'cart-icon-bubble']
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Update cart count
      this.updateCartCount();
      
      // Open cart drawer
      this.openCartDrawer();
      
      // Update cart drawer contents
      this.updateCartDrawer();
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Fallback to traditional form submission
      this.fallbackAddToCart(variantId, quantity);
      
    } finally {
      // Reset button state after delay
      setTimeout(() => {
        this.hideLoadingState();
      }, 1000);
    }
  }
  
  showLoadingState() {
    this.addToCartBtn.disabled = true;
    if (this.buttonText) {
      this.buttonText.style.display = 'none';
    }
    if (this.buttonLoading) {
      this.buttonLoading.style.display = 'inline';
    }
  }
  
  hideLoadingState() {
    this.addToCartBtn.disabled = false;
    if (this.buttonText) {
      this.buttonText.style.display = 'inline';
    }
    if (this.buttonLoading) {
      this.buttonLoading.style.display = 'none';
    }
  }
  
  updateCartCount() {
    // Use Shopify's built-in cart update if available
    if (typeof Shopify !== 'undefined') {
      Shopify.updateCartInfo();
    }
    
    // Update all cart count elements
    const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, #CartCount');
    if (cartCountElements.length) {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          cartCountElements.forEach(element => {
            element.textContent = cart.item_count;
            element.setAttribute('data-cart-count', cart.item_count);
          });
        })
        .catch(console.error);
    }
  }
  
  openCartDrawer() {
    // Try different selectors for cart drawer
    const cartDrawerSelectors = [
      'cart-drawer',
      '.js-cart-drawer',
      '#CartDrawer',
      '.drawer--cart',
      '.drawer[data-drawer="cart"]',
      'details[open].cart-drawer',
      '.cart-drawer[open]'
    ];
    
    for (const selector of cartDrawerSelectors) {
      const cartDrawer = document.querySelector(selector);
      if (cartDrawer) {
        // Different themes use different methods
        if (cartDrawer.tagName === 'CART-DRAWER') {
          // Modern cart drawer component
          if (cartDrawer.open) return;
          cartDrawer.open = true;
        } else if (cartDrawer.tagName === 'DETAILS') {
          // Details element
          cartDrawer.setAttribute('open', '');
        } else {
          // Regular div with classes
          cartDrawer.classList.add('is-open', 'open');
          cartDrawer.setAttribute('aria-hidden', 'false');
          document.body.classList.add('drawer-open', 'overflow-hidden');
        }
        
        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('cart:open'));
        return;
      }
    }
    
    // If no cart drawer found, trigger cart icon click
    const cartIcon = document.querySelector('.header__icon--cart, .cart-link, [data-cart-toggle]');
    if (cartIcon) {
      cartIcon.click();
    }
  }
  
  updateCartDrawer() {
    // Fetch and update cart drawer contents
    fetch('/cart?section_id=cart-drawer')
      .then(response => response.text())
      .then(text => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const cartDrawer = document.querySelector('cart-drawer, .cart-drawer, #CartDrawer');
        if (cartDrawer) {
          const newContent = html.querySelector('cart-drawer, .cart-drawer, #CartDrawer');
          if (newContent) {
            cartDrawer.innerHTML = newContent.innerHTML;
          }
        }
      })
      .catch(console.error);
  }
  
  fallbackAddToCart(variantId, quantity) {
    // Create and submit a form as fallback
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/cart/add';
    form.style.display = 'none';
    
    const idInput = document.createElement('input');
    idInput.type = 'hidden';
    idInput.name = 'id';
    idInput.value = variantId;
    form.appendChild(idInput);
    
    const qtyInput = document.createElement('input');
    qtyInput.type = 'hidden';
    qtyInput.name = 'quantity';
    qtyInput.value = quantity;
    form.appendChild(qtyInput);
    
    document.body.appendChild(form);
    form.submit();
  }
}

// Initialize all product blocks on page load
document.addEventListener('DOMContentLoaded', function() {
  // Check if theme strings are available
  if (!theme.strings) {
    theme.strings = {
      addToCart: 'Add to cart',
      soldOut: 'Sold out',
      adding: 'Adding...'
    };
  }
  
  // Initialize existing product blocks
  document.querySelectorAll('.product-block').forEach(block => {
    new ProductBlockCart(block);
  });
  
  // Handle dynamically loaded product blocks
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          if (node.matches('.product-block')) {
            new ProductBlockCart(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('.product-block').forEach(block => {
              if (!block.dataset.cartInitialized) {
                block.dataset.cartInitialized = 'true';
                new ProductBlockCart(block);
              }
            });
          }
        }
      });
    });
  });
  
  observer.observe(document.body, { childList: true, subtree: true });
});